import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Plus, Pencil, Power, FileText, ChevronRight, Sparkles, PenLine, Image as ImageIcon } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { LocationSelect, useLocations } from "@/components/LocationSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TemplateBuilder } from "@/components/planillas/TemplateBuilder";
import { ImportDialog } from "@/components/planillas/ImportDialog";
import { PlanillaDetail } from "@/components/planillas/PlanillaDetail";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export default function Planillas({ isAdmin = false }) {
  const { activeLocation } = useAuth();
  return (
    <div className={isAdmin ? "" : "px-4 pt-5"}>
      <PageHeader title="Planillas" subtitle="Controles y registros operacionales" right={<LocationSelect />} />
      <Tabs defaultValue="hoy">
        <TabsList className="mb-4">
          <TabsTrigger value="hoy" data-testid="tab-planillas-hoy">Hoy</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-planillas-historial">Historial</TabsTrigger>
          {isAdmin && <TabsTrigger value="plantillas" data-testid="tab-planillas-plantillas">Plantillas</TabsTrigger>}
        </TabsList>
        <TabsContent value="hoy"><Hoy locationId={activeLocation} /></TabsContent>
        <TabsContent value="historial"><Historial isAdmin={isAdmin} /></TabsContent>
        {isAdmin && <TabsContent value="plantillas"><Plantillas /></TabsContent>}
      </Tabs>
    </div>
  );
}

function Hoy({ locationId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  useEffect(() => {
    if (!locationId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    api.get(`/planillas/today?location_id=${locationId}`).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [locationId]);

  const openFill = async (it) => {
    const { data: tpl } = await api.get(`/planillas/templates/${it.template_id}`);
    nav("/planillas/llenar", { state: { template: tpl, location_id: locationId } });
  };

  if (loading) return <div className="py-10 text-center text-sm text-slate-400">Cargando…</div>;
  if (!locationId) return <EmptyState icon={ClipboardList} title="Selecciona un local" />;
  if (items.length === 0) return <EmptyState icon={ClipboardList} title="Sin planillas" subtitle="No hay planillas programadas hoy." />;
  return (
    <div className="space-y-3">
      {items.map((it) => {
        const done = ["COMPLETED", "OBSERVATION", "INCIDENT"].includes(it.status);
        return (
          <div key={it.template_id} data-testid={`planilla-today-${it.template_id}`} className="flex items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-[#153D35]">{it.name}</p>
              <p className="truncate text-xs text-[#68756F]">{it.category}{it.area_name ? ` · ${it.area_name}` : ""}{it.target_time ? ` · ${it.target_time}` : ""}</p>
              <div className="mt-2 flex items-center gap-2"><StatusPill status={it.status} />{it.incidents > 0 && <span className="text-xs font-medium text-red-600">{it.incidents} incidencia(s)</span>}</div>
            </div>
            <Button data-testid={`fill-btn-${it.template_id}`} onClick={() => openFill(it)} className="h-11 shrink-0 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">{done ? "Repetir" : "Completar"}</Button>
          </div>
        );
      })}
    </div>
  );
}

function Historial({ isAdmin }) {
  const { locations } = useLocations();
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ location_id: "", status: "", date_from: "", date_to: "" });
  const [detailId, setDetailId] = useState(null);
  const load = () => {
    const p = new URLSearchParams({ limit: "200" });
    Object.entries(f).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get(`/planillas/submissions?${p}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [f]); // eslint-disable-line
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={f.location_id || "all"} onValueChange={(v) => setF({ ...f, location_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="pl-hist-loc" className="h-10 w-40 bg-white"><SelectValue placeholder="Local" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los locales</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="pl-hist-status" className="h-10 w-40 bg-white"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="COMPLETED">Completada</SelectItem><SelectItem value="OBSERVATION">Con observación</SelectItem><SelectItem value="INCIDENT">Incidencia</SelectItem></SelectContent>
        </Select>
        <Input data-testid="pl-hist-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-36" />
        <Input data-testid="pl-hist-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-36" />
      </div>
      {items.length === 0 ? <EmptyState icon={FileText} title="Sin registros" /> : (
        <div className="space-y-3">
          {items.map((s) => (
            <button key={s.id} data-testid={`pl-hist-${s.id}`} onClick={() => setDetailId(s.id)} className="flex w-full items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4 text-left hover:shadow-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-[#153D35]">{s.template_name}</p>
                <p className="truncate text-xs text-[#68756F]">{fmtDate(s.created_at)} · {s.location_name} · {s.area_name || "—"}{isAdmin ? ` · ${s.user_name}` : ""}</p>
                <div className="mt-2"><StatusPill status={s.status} /></div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300" />
            </button>
          ))}
        </div>
      )}
      <PlanillaDetail submissionId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function Plantillas() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [builder, setBuilder] = useState({ open: false, id: null, draft: null, importId: null, reviewMode: false });
  const [showChoice, setShowChoice] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const load = () => api.get("/planillas/templates?include_inactive=true").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/planillas/categories").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);
  const deactivate = async (id) => { await api.post(`/planillas/templates/${id}/deactivate`); load(); };

  const openManual = () => { setShowChoice(false); setBuilder({ open: true, id: null, draft: null, importId: null, reviewMode: false }); };
  const openImport = () => { setShowChoice(false); setShowImport(true); };
  const onDraft = (draft, importId) => { setShowImport(false); setBuilder({ open: true, id: null, draft, importId, reviewMode: true }); };
  const closeBuilder = () => setBuilder({ open: false, id: null, draft: null, importId: null, reviewMode: false });

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button data-testid="new-template-btn" onClick={() => setShowChoice(true)} className="rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]"><Plus className="mr-2 h-4 w-4" /> Nueva plantilla</Button>
      </div>
      {items.length === 0 ? <EmptyState icon={ClipboardList} title="Sin plantillas" subtitle="Crea tu primera plantilla configurable." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <div key={t.id} data-testid={`template-${t.id}`} className={`rounded-2xl border bg-white p-4 ${t.active ? "border-[#E2E8F0]" : "opacity-60"}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-medium text-[#153D35]">{t.name}</p><p className="text-xs text-[#68756F]">{t.category} · {t.frequency} · v{t.version}</p></div>
                <span className="text-xs text-slate-400">{(t.sections || []).reduce((n, s) => n + s.questions.length, 0)} preg.</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(t.sections || []).some((s) => s.repeatable) && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-[#1D4ED8]">Repetible</span>}
                {t.reference_image_path && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"><ImageIcon className="h-3 w-3" /> Referencia</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`edit-template-${t.id}`} size="sm" variant="outline" onClick={() => setBuilder({ open: true, id: t.id, draft: null, importId: null, reviewMode: false })} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
                {t.active && <Button size="sm" variant="ghost" onClick={() => deactivate(t.id)} className="rounded-lg text-slate-500"><Power className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showChoice} onOpenChange={setShowChoice}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Nueva plantilla</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <button data-testid="choice-manual" onClick={openManual} className="flex flex-col items-start gap-2 rounded-2xl border border-[#E2E8F0] p-4 text-left transition-colors hover:border-[#2563EB] hover:bg-[#F8FAFC]">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-[#2563EB]"><PenLine className="h-5 w-5" /></span>
              <span className="font-medium text-[#153D35]">Crear manualmente</span>
              <span className="text-xs text-[#68756F]">Construye una planilla desde cero.</span>
            </button>
            <button data-testid="choice-import" onClick={openImport} className="flex flex-col items-start gap-2 rounded-2xl border border-[#E2E8F0] p-4 text-left transition-colors hover:border-[#2563EB] hover:bg-[#F8FAFC]">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-[#2563EB]"><Sparkles className="h-5 w-5" /></span>
              <span className="font-medium text-[#153D35]">Importar desde imagen</span>
              <span className="text-xs text-[#68756F]">Sube una planilla existente y TUPU preparará un borrador para ti.</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ImportDialog open={showImport} onClose={() => setShowImport(false)} onDraft={onDraft} />
      <TemplateBuilder templateId={builder.id} open={builder.open} cats={cats} initialDraft={builder.draft} importId={builder.importId} reviewMode={builder.reviewMode} onClose={closeBuilder} onSaved={load} />
    </div>
  );
}
